import { useEffect, useState } from 'react';
import useCreateAdminOrganization from '../../api/useCreateAdminOrganization';
import useFetchAdminPlans from '../../api/useFetchAdminPlans';
import useUpdateAdminOrganizationPlan from '../../api/useUpdateAdminOrganizationPlan';
import { AdminButton } from '../AdminButton';
import AdminFormInput from '../atoms/AdminFormInput';
import AdminFormSelect from '../atoms/AdminFormSelect';
import AdminErrorMessage from '../atoms/AdminErrorMessage';
import AdminModal from '../molecules/AdminModal';

type AdminOrganizationCreateModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function AdminOrganizationCreateModal({
  open,
  onClose,
}: AdminOrganizationCreateModalProps) {
  const {
    data: plans = [],
    isLoading: isPlansLoading,
    isError: isPlansError,
  } = useFetchAdminPlans();
  const { mutateAsync: createOrganization, isPending: isCreating } = useCreateAdminOrganization();
  const { mutateAsync: updatePlan, isPending: isUpdatingPlan } = useUpdateAdminOrganizationPlan();

  const [companyName, setCompanyName] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [planId, setPlanId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [memberCounts, setMemberCounts] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCompanyName('');
      setInviteName('');
      setInviteEmail('');
      setPlanId(plans[0]?.id ?? null);
      setStartDate('');
      setEndDate('');
      setMemberCounts('');
      setError(null);
    }
  }, [open, plans]);

  const handleSubmit = async () => {
    if (!companyName.trim()) {
      setError('企業名を入力してください');
      return;
    }
    if (!inviteName.trim()) {
      setError('招待ユーザー名を入力してください');
      return;
    }
    if (!inviteEmail.trim()) {
      setError('招待メールアドレスを入力してください');
      return;
    }
    if (!planId) {
      setError('プランを選択してください');
      return;
    }
    const memberCountsValue = memberCounts.trim() === '' ? null : Number(memberCounts);
    if (memberCountsValue !== null && Number.isNaN(memberCountsValue)) {
      setError('契約人数は数値で入力してください');
      return;
    }

    try {
      const organization = await createOrganization({
        companyName: companyName.trim(),
        name: inviteName.trim(),
        email: inviteEmail.trim(),
      });
      await updatePlan({
        organizationId: organization.id,
        planId,
        startDate,
        endDate,
        memberCounts: memberCountsValue,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '作成に失敗しました');
    }
  };

  const isPending = isCreating || isUpdatingPlan;

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="クライアントの追加"
      footer={
        <div className="flex justify-end">
          <AdminButton
            label={isPending ? '作成中...' : '作成する'}
            disabled={isPending}
            onClick={handleSubmit}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <AdminFormInput
          label="企業名"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
        <div className="flex gap-4">
          <div className="flex-1">
            <AdminFormInput
              label="招待ユーザー名"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <AdminFormInput
              label="招待メールアドレス"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>
        </div>
        <AdminFormSelect
          label="プラン"
          value={planId ?? ''}
          onChange={(e) => setPlanId(Number(e.target.value))}
          isLoading={isPlansLoading}
          isError={isPlansError}
        >
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </AdminFormSelect>
        <div className="flex gap-4">
          <div className="flex-1">
            <AdminFormInput
              label="契約開始日"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <AdminFormInput
              label="契約終了日"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <AdminFormInput
          label="契約人数"
          type="number"
          min={1}
          value={memberCounts}
          onChange={(e) => setMemberCounts(e.target.value)}
        />
        <AdminErrorMessage message={error} />
      </div>
    </AdminModal>
  );
}
